import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

interface MobileNavCtx {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const Ctx = createContext<MobileNavCtx | null>(null);

/**
 * Tiny provider that owns the mobile sidebar drawer's open/close state.
 *
 * Pulled into its own context (rather than co-located with `Sidebar`) so the
 * Topbar hamburger button and the drawer itself can share state without
 * either having to know about the other's mount point. Auto-closes on route
 * change so navigating from the drawer feels instant.
 */
export function MobileNavProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.search]);

  const value = useMemo<MobileNavCtx>(() => ({ isOpen, open, close, toggle }), [isOpen, open, close, toggle]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useMobileNav(): MobileNavCtx {
  const ctx = useContext(Ctx);
  if (!ctx) {
    return { isOpen: false, open: () => {}, close: () => {}, toggle: () => {} };
  }
  return ctx;
}
